/* eslint-disable @angular-eslint/no-output-on-prefix */
import { Component, EventEmitter, Input, OnChanges, Output, OnInit } from '@angular/core';
import { QueryParamService } from '../../services/query-param.service';
import { TerminologyService } from '../../services/terminology.service';
import { BrowserUtilityService } from '../../services/browser-utility.service';

@Component({
  standalone: false,
  selector: 'app-hierarchy-tree',
  templateUrl: './hierarchy-tree.component.html',
  styleUrls: ['./hierarchy-tree.component.css'],
})
export class HierarchyTreeComponent implements OnInit, OnChanges {
  public nodes: any[] = [];
  public treeNodesHD: any[] = [];
  public collapseAttr = 'isCollapsed';
  public selectAttr = 'isSelected';
  public inDeterminateAttr = 'isIndeterminate';
  private nodeOffsets: Map<string, number> = new Map();
  private nodeShowAll: Map<string, boolean> = new Map();
  private nodeChildrenTotal: Map<string, number> = new Map();

  @Input() data: any[];
  @Input() prepareData = true;
  @Input() idAttr = 'ID';
  @Input() parentAttr = 'PARENT_ID';
  @Input() childrenAttr = 'CHILDREN';
  @Input() currentProject: any;
  @Input() currentOrgUriLabel: string;
  @Input() selectedConcept: any;
  @Input() terminologies: Array<any>;
  @Output() onChange = new EventEmitter<any>();
  @Output() onClick = new EventEmitter<any>();

  icons: any;
  originalNode: any;
  currentOffset = 0;
  selectedConceptParent: any;
  loadMoreAll = false;
  includeCollapseSearch = false;

  constructor(
    private readonly queryParamService: QueryParamService,
    private readonly terminologyService: TerminologyService,
    readonly browserUtility: BrowserUtilityService,
  ) {
    this._recursiveUpdate([this.nodes], 'selectedTreeNode', true);

    if (this.nodes?.length > 0) {
      this._recursiveFindParent([this.nodes]);
    }
    if (this.selectedConceptParent) {
      this.getConceptByCode(this.selectedConceptParent);
    }

    if (this.nodes.length > 0) {
      this.nodes = this.nodes.map((node) => {
        node.link = this.getBrowserLink();
        return node;
      });
    }
  }

  ngOnInit(): void {
    this._recursiveUpdate([this.nodes], 'selectedTreeNode', true);

    if (this.nodes?.length > 0) {
      this._recursiveFindParent([this.nodes]);
    }
    if (this.selectedConceptParent) {
      this.getConceptByCode(this.selectedConceptParent);
    }

    if (this.nodes.length > 0) {
      this.nodes = this.nodes.map((node) => {
        node.link = this.getBrowserLink();
        return node;
      });
    }
  }

  ngOnChanges(changes) {
    this.nodes = [];
    if (this.selectedConcept !== null) {
      if (changes.data?.currentValue && this.selectedConcept?.active) {
        if (Object.keys(this.data).length !== 0) {
          this.nodeOffsets = new Map();
          this.selectedConceptParent = null;
          this.nodes = this.createTreeData(this.data);
          this.originalNode = this.nodes[0];
        }
      }
    }
  }

  private _recursiveUpdate(list, attr, value) {
    if (Array.isArray(list)) {
      for (let i = 0, len = list.length; i < len; i++) {
        if (list[i][0] !== undefined) {
          if (list[i][0][attr] === undefined) {
            (list[i][0] as any)[attr] = null;
          }
          list[i][0][attr] = value;
          if (list[i][0].concept.childCt > 0) {
            if (list[i][0].concept.children !== undefined) {
              if (list[i][0].concept.children.length) {
                this._recursiveUpdate(list[i][0].concept.children, attr, value);
              }
            }
          }
        }
      }
    }
  }

  private _recursiveFindParent(list) {
    if (Array.isArray(list)) {
      for (let i = 0, len = list.length; i < len; i++) {
        for (let k = 0, len = this.selectedConcept.parents?.length; k < len; k++) {
          if (list[i][0] !== undefined) {
            if (list[i][0][this.idAttr] === this.selectedConcept.parents[k].code) {
              this.selectedConceptParent = list[i][0];
            }
          }
        }
        if (list[i][0] !== undefined) {
          if (list[i][0].concept.childCt > 0 && this.selectedConceptParent === null) {
            if (list[i][0].concept.children !== undefined) {
              if (list[i][0].concept.children.length) {
                this._recursiveFindParent(list[i][0].concept.children);
              }
            }
          }
        }
      }
    }
  }

  getBrowserLink(): string {
    let link: string;
    if (!this.currentProject.public) {
      link = `/organizations/${this.currentOrgUriLabel}/projects/${this.currentProject?.uriLabel}/browser`;
    } else {
      link = `/projects/${this.currentProject?.uriLabel}/browser`;
    }
    return link;
  }

  createTreeData(tree: any, parentId?: string): { ID: string; NAME: string; PARENT_ID?: string; concept: any }[] {
    const treeData = [];
    if (tree?.concept?.code && tree?.concept?.name) {
      treeData.push({
        ID: tree.concept.code,
        NAME: tree.concept.name,
        SHOW: true,
        link: this.getBrowserLink(),
        loading: false,
        ...(parentId ? { PARENT_ID: parentId } : {}),
        concept: { ...tree, showAllChildren: false, childrenShown: 9 },
      });

      if (Array.isArray(tree.children)) {
        for (let j = 0, len = tree.children.length; j < len; j++) {
          treeData[0].concept.children[j] = this.createTreeData(tree.children[j], tree.concept.code);
        }
      }
    }
    return this.getUniqueNodes(treeData);
  }

  getConceptQueryParams(concept: any): object {
    let queryParams = JSON.parse(JSON.stringify(this.queryParamService.currentQueryParams));
    if (queryParams === null) {
      queryParams = [];
    }
    let newLinkParams = {};
    newLinkParams = {
      ...newLinkParams,
      selectedTerminologies: queryParams?.selectedTerminologies,
      code: concept.code,
      terminology: this.selectedConcept.terminology,
      publisher: this.selectedConcept.publisher,
      version: this.selectedConcept.version,
      currentPage: 1,
    };
    if (this.includeCollapseSearch) {
      newLinkParams = {
        ...newLinkParams,
        collapseSearch: true,
      };
    }
    return newLinkParams;
  }

  onMouseClick(event: any, include: boolean) {
    this.includeCollapseSearch = include;
  }

  getUniqueNodes(nodes) {
    const idNameSet = new Set();
    return nodes.filter((node) => {
      const key = `${node.ID}-${node.NAME}`;
      const hasKey = idNameSet.has(key);
      idNameSet.add(key);
      return !hasKey;
    });
  }

  onModelChange(node) {
    if (node[this.childrenAttr].length) {
      this._recursiveEdit([node], this.childrenAttr, this.selectAttr, node[this.selectAttr]);
    }
    this.onChange.emit(node);
  }

  change(value: any) {
    const parent = this.nodes.filter((item) => {
      return item.ID === value[this.parentAttr];
    })[0];
    if (parent) {
      const duplicate = {};
      let isIndeterminate = value[this.inDeterminateAttr] || false;

      parent[this.childrenAttr].forEach((item) => {
        duplicate[item[this.selectAttr]] = (duplicate[item[this.selectAttr]] || 0) + 1;
        if (item[this.inDeterminateAttr]) {
          isIndeterminate = true;
        }
      });
      if (Object.keys(duplicate).length === 1 && !isIndeterminate) {
        parent[this.inDeterminateAttr] = false;
        parent[this.selectAttr] = JSON.parse(Object.keys(duplicate)[0]);
        this.onChange.emit(parent);
      } else {
        parent[this.inDeterminateAttr] = true;
        this.onChange.emit(parent);
      }
    }
  }

  private _recursiveEdit(list, childrenAttr, attr, value) {
    if (Array.isArray(list)) {
      for (let i = 0, len = list.length; i < len; i++) {
        list[i][attr] = value;
        if (list[i][childrenAttr].length) {
          this._recursiveEdit(list[i][childrenAttr], childrenAttr, attr, value);
        }
      }
    }
  }

  getLevel(node) {
    let level = 0;
    let current = node;

    if (node.ID !== this.originalNode.ID) {
      while (current?.PARENT_ID) {
        level++;
        current = this.nodes.find((n) => n.ID === current.PARENT_ID);
      }
    }

    return level;
  }

  expandNode(node) {
    if (!node.concept.childrenLoaded) {
      node.concept.expanded = true;
      this.getConceptByCode(node);
    } else {
      node.concept.expanded = true;
      this._recursiveShow(node.concept.children, 'SHOW', true);
    }
  }

  collapseNode(node) {
    node.concept.expanded = false;
    this._recursiveShow(node.concept.children, 'SHOW', false);
  }

  private _recursiveShow(list, attr, value) {
    if (Array.isArray(list)) {
      for (let i = 0, len = list.length; i < len; i++) {
        if (list[i][0][attr] === undefined) {
          (list[i][0] as any)[attr] = null;
        }
        list[i][0][attr] = value;
        if (list[i][0].concept.childCt > 0 && list[i][0].concept.childrenLoaded) {
          if (list[i][0].concept.children !== undefined) {
            if (list[i][0].concept.children.length) {
              list[i][0].concept.expanded = value;
              this._recursiveShow(list[i][0].concept.children, attr, value);
            }
          }
        }
      }
    }
  }

  clickPlus(node: any) {
    if (!node.loading) {
      if (node.concept.childCt < 100 && node.ID === 'root') {
        this.getFakeRootConceptChildTreeData(node);
      } else {
        this.getConceptByCode(node);
      }
    }
  }

  click(concept) {
    this.onClick.emit(concept);
  }

  getFakeRootConceptChildTreeData(node, defaultLimit = 100, offset = 0) {
    const nodeId = node?.concept?.concept?.code;
    const currentOffset = this.nodeOffsets.get(nodeId) || offset;
    let sortBy = 'concept.name';
    if (isNaN(nodeId)) {
      sortBy = 'concept.code';
    }
    this.terminologyService
      .getTopLevelConceptTreeChildrenPositions(this.currentProject?.id, node.concept.terminology, node.ID, defaultLimit, currentOffset, true, sortBy)
      .subscribe((data) => {
        const children = [];
        data.items?.forEach((item) => children.push(this.createTreeData(item)[0]));
        this._recuriveAddChildren([this.nodes], nodeId, children, sortBy);
        this.nodeChildrenTotal.set(nodeId, data.total || 0);
        node.concept.childrenLoaded = true;
        node.concept.expanded = true;
      });
  }

  getConceptByCode(node, defaultLimit = 10, offset = 0): void {
    let terminologyId;
    this.terminologies.forEach((term) => {
      if (term.abbreviation == node?.concept?.terminology && term.publisher == node?.concept?.publisher && term.version == node?.concept?.version) {
        terminologyId = term.id;
      }
    });
    node.loading = true;
    const code = this.browserUtility.escapeSafeSearch(node?.concept?.concept?.code);
    const searchObservable = this.terminologyService.getBrowserSearchResults(
      this.currentProject?.id,
      `code:${code}`,
      terminologyId,
      '1',
      '0',
      false,
      false,
    );
    searchObservable.subscribe((results) => {
      if (results?.items) {
        const nodeId = node?.concept?.concept?.code;
        const currentOffset = this.nodeOffsets.get(nodeId) || offset;
        let sortBy = 'concept.name';
        if (isNaN(nodeId)) {
          sortBy = 'concept.code';
        }
        this.terminologyService
          .getConceptTreeChildrenPositions(this.currentProject?.id, results?.items[0]?.id, defaultLimit, currentOffset, true, sortBy)
          .subscribe((data) => {
            const children = [];
            if (data?.items?.length > 0) {
              data.items?.forEach((item) => children.push(this.createTreeData(item)[0]));
              if (this.nodes !== null && this.nodes !== undefined && this.nodes.length > 0) {
                this._recuriveAddChildren([this.nodes], nodeId, children, sortBy);
              }
              this.nodeChildrenTotal.set(nodeId, data.total || 0);
              node.concept.childrenLoaded = true;
              node.concept.expanded = true;
            } else {
              node.concept.childCt = 0;
            }
            node.loading = false;
            this.loadMoreAll = false;
          });
      } else {
        node.loading = false;
        this.loadMoreAll = false;
      }
    });
  }

  romanToInt(s: string): number {
    const romanMap: { [key: string]: number } = {
      I: 1,
      V: 5,
      X: 10,
      L: 50,
      C: 100,
      D: 500,
      M: 1000,
    };
    let result = 0;
    for (let i = 0; i < s.length; i++) {
      const currentVal = romanMap[s[i]];
      const nextVal = romanMap[s[i + 1]];
      if (nextVal > currentVal) {
        result += nextVal - currentVal;
        i++;
      } else {
        result += currentVal;
      }
    }
    return result;
  }

  private _recuriveAddChildren(list, parentNodeId, children, sortBy) {
    if (Array.isArray(list)) {
      for (let i = 0, len = list.length; i < len; i++) {
        if (list[i][0] !== undefined) {
          if (list[i][0][this.idAttr] === parentNodeId) {
            for (const child of children) {
              let replacement = false;
              if (list[i][0].concept.children !== undefined) {
                for (let k = 0, len = list[i][0].concept.children.length; k < len; k++) {
                  if (child[this.idAttr] === list[i][0].concept.children[k][0][this.idAttr]) {
                    replacement = true;
                  }
                }
              } else {
                list[i][0].concept.children = [];
              }
              if (!replacement) {
                child[this.parentAttr] = parentNodeId;
                list[i][0].concept.children.push([child]);
              }
            }
            const romanRegex = /^M{0,3}(CM|CD|D?C{0,3})(XC|XL|L?X{0,3})(IX|IV|V?I{0,3})$/;
            const foundRoman = romanRegex.test(list[i][0].concept.children[0][0].ID);
            if (foundRoman) {
              list[i][0].concept.children = list[i][0].concept.children.sort((a, b) => {
                const intA = this.romanToInt(a[0].ID);
                const intB = this.romanToInt(b[0].ID);
                return intA - intB;
              });
            } else {
              if (sortBy === 'concept.name') {
                list[i][0].concept.children = list[i][0].concept.children.sort((a, b) => {
                  if (a[0].NAME < b[0].NAME) {
                    return -1;
                  }
                  if (a[0].NAME > b[0].NAME) {
                    return 1;
                  }
                  return 0;
                });
              } else {
                list[i][0].concept.children = list[i][0].concept.children.sort((a, b) => {
                  if (a[0].ID < b[0].ID) {
                    return -1;
                  }
                  if (a[0].ID > b[0].ID) {
                    return 1;
                  }
                  return 0;
                });
              }
            }
          } else {
            if (list[i][0].concept.childCt > 0) {
              if (list[i][0].concept.children !== undefined) {
                if (list[i][0].concept.children.length > 0) {
                  this._recuriveAddChildren(list[i][0].concept.children, parentNodeId, children, sortBy);
                }
              }
            }
          }
        }
      }
    }
  }

  showMore(node: any): void {
    this.loadMoreAll = true;
    const nodeId = node?.concept?.concept?.code;
    const currentOffset = (this.nodeOffsets.get(nodeId) || 0) + 10;
    this.nodeOffsets.set(nodeId, currentOffset);
    this.getConceptByCode(node, 10, currentOffset);
  }

  showAll(node: any): void {
    this.loadMoreAll = true;
    const nodeId = node?.concept?.concept?.code;
    const currentOffset = node.concept.children?.length;
    this.nodeOffsets.set(nodeId, currentOffset);
    this.nodeShowAll.set(nodeId, true);
    this.getConceptByCode(node, this.getTotalChildrenForNode(node), currentOffset);
  }

  getTotalChildrenForNode(node: any): number {
    return this.nodeChildrenTotal.get(node?.concept?.concept?.code) || 0;
  }

  getShowAllForNode(node: any): boolean {
    return this.nodeShowAll.get(node?.concept?.concept?.code) || false;
  }

  getChildLevel(node: any): number {
    return this.getLevel(node) + 1;
  }
}
